// Pestaña Clientes del portal: filtros · cartera (`GET /portal/customers`, 25 por página, alfabética) · ficha
// (`GET /portal/customers/{id}`). El servidor solo busca por nombre; lo demás filtra lo cargado.
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useLoadedPaginationTable, type LoadedScope } from '@/shared/patterns/LoadedScope';
import { MasterDetailExplorer, type ExplorerSplit } from '@/shared/patterns/MasterDetail';
import { usePortalCustomer, usePortalCustomers } from './api/usePortalCustomers';
import { CustomerDetailPanel } from './components/CustomerDetailPanel';
import { CustomersFiltersPanel } from './components/CustomersFiltersPanel';
import { CustomersTable } from './components/CustomersTable';
import {
    customersEmptyNote,
    customersListErrorCopy,
    customersNextPageReason,
    loadedCustomersNote,
    pillCounts,
} from './lib/portalCustomersCopy';
import {
    countActiveFilters,
    DEFAULT_SORT,
    EMPTY_FILTERS,
    filterBySegment,
    filterCustomers,
    localOnlyFilterLabels,
    segmentCounts,
    serverQueryOf,
    sortCustomers,
    type CustomersSegment,
    type PortalCustomersFilters,
    type SortState,
} from './lib/portalCustomersFilters';

/** A 1440px: unos 650px de cartera para sus ocho columnas contra 350px de ficha. */
const CUSTOMERS_SPLIT: ExplorerSplit = { list: 'minmax(0,13fr)', detail: 'minmax(340px,7fr)' };

/** Espera tras la última tecla antes de mandar la búsqueda al servidor. */
const SEARCH_DEBOUNCE_MS = 300;

export function CustomersExplorer({
    customerId,
    className,
}: {
    customerId?: string;
    className?: string;
}) {
    const navigate = useNavigate();
    const [filters, setFilters] = useState<PortalCustomersFilters>(EMPTY_FILTERS);
    const [segment, setSegment] = useState<CustomersSegment>('all');
    const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
    const [selectedId, setSelectedId] = useState<string | null>(customerId ?? null);
    const [linkedId, setLinkedId] = useState(customerId);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    // El deep-link `?customer=` manda sobre lo elegido a mano.
    if (customerId !== linkedId) {
        setLinkedId(customerId);
        setSelectedId(customerId ?? null);
    }

    // El nombre filtra lo cargado al instante y viaja al servidor al dejar de teclear.
    const debouncedFilters = useDebounce(filters, SEARCH_DEBOUNCE_MS);
    const serverQuery = useMemo(() => serverQueryOf(debouncedFilters), [debouncedFilters]);
    const list = usePortalCustomers(serverQuery);

    const panelRows = useMemo(() => filterCustomers(list.rows, filters), [list.rows, filters]);
    const visibleRows = useMemo(
        () => sortCustomers(filterBySegment(panelRows, segment), sort),
        [panelRows, segment, sort],
    );
    const table = useLoadedPaginationTable(visibleRows);
    const modelRows = table.getRowModel().rows;
    const pageRows = useMemo(() => modelRows.map((row) => row.original), [modelRows]);

    const scope: LoadedScope = { loaded: list.rows.length, hasMore: list.hasNextPage };
    const pills = pillCounts(segmentCounts(panelRows), scope);
    const loadedNote = loadedCustomersNote({
        scope,
        localFilters: localOnlyFilterLabels(filters),
        localSort: sort.key !== DEFAULT_SORT.key || sort.direction !== DEFAULT_SORT.direction,
    });
    const hasActiveFilters = countActiveFilters(filters) > 0 || segment !== 'all';
    const nameQuery =
        serverQuery.q !== undefined && countActiveFilters(filters) === 1 && segment === 'all'
            ? serverQuery.q
            : null;

    /**
     * La ficha abierta: el enlace siempre, aunque no esté cargado; la elegida mientras siga visible;
     * si no, la primera visible. Con el enlace roto se abre la nada, nunca otra ficha.
     */
    const openedId = useMemo(() => {
        if (selectedId !== null && selectedId === linkedId) return selectedId;
        if (visibleRows.some((row) => row.retailCustomerId === selectedId)) return selectedId;
        return visibleRows[0]?.retailCustomerId ?? null;
    }, [visibleRows, selectedId, linkedId]);
    const openedRow = list.rows.find((row) => row.retailCustomerId === openedId);
    const accountOnlyRow = openedRow?.accountOnly ? openedRow : null;
    const customer = usePortalCustomer(accountOnlyRow ? null : openedId);

    /** Elegir una fila la abre y la deja en `?customer=`, para que recargar o compartir la conserve. */
    const selectRow = (retailCustomerId: string) => {
        setSelectedId(retailCustomerId);
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { customer: retailCustomerId },
            replace: true,
        });
    };

    return (
        <MasterDetailExplorer
            className={className}
            split={CUSTOMERS_SPLIT}
            filtersCollapsed={filtersCollapsed}
            filters={
                <CustomersFiltersPanel
                    filters={filters}
                    onChange={setFilters}
                    partialScope={list.hasNextPage}
                    collapsed={filtersCollapsed}
                    onToggleCollapsed={() => {
                        setFiltersCollapsed((v) => !v);
                    }}
                />
            }
            list={
                <CustomersTable
                    table={table}
                    pageRows={pageRows}
                    shownCount={visibleRows.length}
                    scope={scope}
                    segment={segment}
                    pills={pills}
                    onSegmentChange={setSegment}
                    loadedNote={loadedNote}
                    emptyNote={customersEmptyNote({
                        hasActiveFilters,
                        hasNextPage: list.hasNextPage,
                        nameQuery,
                    })}
                    creditUnavailableCount={list.creditUnavailableCount}
                    sort={sort}
                    onSortChange={setSort}
                    selectedId={openedId}
                    onSelect={selectRow}
                    isLoading={list.isPending}
                    errorCopy={list.error ? customersListErrorCopy(list.error) : null}
                    onRetry={list.retry}
                    hasNextPage={list.hasNextPage}
                    isFetchingNextPage={list.isFetchingNextPage}
                    onLoadMore={list.mustRestart ? list.restart : list.fetchNextPage}
                    nextPageError={
                        list.nextPageError === null
                            ? null
                            : customersNextPageReason(list.nextPageError)
                    }
                />
            }
            detail={
                <CustomerDetailPanel
                    key={openedId ?? 'none'}
                    detail={customer.detail}
                    accountOnlyRow={accountOnlyRow}
                    isLoading={customer.isLoading}
                    notFound={customer.notFound}
                    failed={customer.failed}
                    onRetry={customer.retry}
                    hasSelection={openedId !== null}
                />
            }
        />
    );
}
